import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../club-api-client";
import type { CreateTrainingSeason, DuplicateSeason, UpdateTrainingSeason } from "../../types";
import { queryKeys } from "../query-keys";

export function useTrainingSeasons(clubId: string | null, status?: string) {
	return useQuery({
		queryKey: queryKeys.clubPortal.trainingSeasons(clubId ?? "", status),
		queryFn: () => clubApi.training.seasons.list(clubId!, status ? { status } : undefined),
		enabled: !!clubId,
	});
}

export function useTrainingSeason(clubId: string | null, seasonId: string | null) {
	return useQuery({
		queryKey: queryKeys.clubPortal.trainingSeason(clubId ?? "", seasonId ?? ""),
		queryFn: () => clubApi.training.seasons.get(clubId!, seasonId!),
		enabled: !!clubId && !!seasonId,
	});
}

export function useCreateTrainingSeason(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateTrainingSeason) => clubApi.training.seasons.create(clubId, data),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training", "seasons"],
			}),
	});
}

export function useUpdateTrainingSeason(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ seasonId, data }: { seasonId: string; data: UpdateTrainingSeason }) =>
			clubApi.training.seasons.update(clubId, seasonId, data),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training", "seasons"],
			}),
	});
}

export function useDeleteTrainingSeason(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (seasonId: string) => clubApi.training.seasons.delete(clubId, seasonId),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training", "seasons"],
			}),
	});
}

export function useGenerateTrainingSessions(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (seasonId: string) => clubApi.training.seasons.generate(clubId, seasonId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			});
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "events"],
			});
		},
	});
}

export function useCheckTrainingConflicts(clubId: string) {
	return useMutation({
		mutationFn: ({
			seasonId,
			data,
		}: {
			seasonId: string;
			data: {
				day_of_week: number;
				start_time: string;
				end_time: string;
				trainer_id: string;
				court_ids: string[];
				exclude_slot_id?: string;
			};
		}) => clubApi.training.seasons.checkConflicts(clubId, seasonId, data),
	});
}

export function usePreviewTrainingSessions(clubId: string) {
	return useMutation({
		mutationFn: (seasonId: string) => clubApi.training.seasons.preview(clubId, seasonId),
	});
}

export function useFinalizeTrainingSeason(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (seasonId: string) => clubApi.training.seasons.finalize(clubId, seasonId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training", "seasons"],
			});
			qc.invalidateQueries({
				queryKey: queryKeys.clubPortal.chatRooms(clubId),
			});
		},
	});
}

export function useDuplicateTrainingSeason(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ seasonId, data }: { seasonId: string; data: DuplicateSeason }) =>
			clubApi.training.seasons.duplicate(clubId, seasonId, data),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training", "seasons"],
			}),
	});
}
